/**
 * @file CORS diagnostics helpers.
 * @description Utilities for probing API CORS behavior with authenticated and preflight requests.
 * @author Sanjay Jangid (Jiggle frontend)
 * @email sanjay.jangid@dgtoohl.com
 * @date 2026-05-10
 * @version 0.0.0
 */
/**
 * CORS Test Utility
 * Helps debug CORS issues by testing API connectivity
 */

export interface CorsTestResult {
  success: boolean;
  error?: string;
  headers?: Record<string, string>;
  status?: number;
  statusText?: string;
  type?: ResponseType;
}

export const testCorsConnection = async (url: string): Promise<CorsTestResult> => {
  try {
    
    // Test 1: Simple GET request with token
    const token = localStorage.getItem('jiggle_token');
    const requestHeaders: HeadersInit = {
      'Accept': 'application/json',
    };
    
    if (token) {
      requestHeaders['Authorization'] = `Bearer ${token}`;
    }
    
    const response = await fetch(url, {
      method: 'GET',
      mode: 'cors',
      credentials: 'include',
      headers: requestHeaders,
    });

    const responseHeaders: Record<string, string> = {};
    response.headers.forEach((value, key) => {
      responseHeaders[key] = value;
    });

    return {
      success: response.ok,
      status: response.status,
      statusText: response.statusText,
      headers: responseHeaders,
      type: response.type,
    };
  } catch (error) {

    // Test 2: Try without credentials
    try {
      const response2 = await fetch(url, {
        method: 'GET',
        mode: 'cors',
        headers: {
          'Accept': 'application/json',
        },
      });
      
      return {
        success: response2.ok,
        status: response2.status,
        statusText: response2.statusText,
        type: response2.type,
        error: 'Original request failed, but request without credentials worked',
      };
    } catch {
      // Handle error silently
    }

    // Test 3: Try OPTIONS preflight
    try {
      const response3 = await fetch(url, {
        method: 'OPTIONS',
        mode: 'cors',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
      });
      
      return {
        success: false,
        status: response3.status,
        statusText: response3.statusText,
        type: response3.type,
        error: `Original failed, OPTIONS returned ${response3.status}`,
      };
    } catch {
      // Handle error silently
    }

    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
};

export const testApiEndpoints = async (baseUrl: string): Promise<void> => {
  const endpoints = [
    'dashboard',
    'upload-history',
    'settings',
    'advertisers',
    'campaigns',
  ];

  
  for (const endpoint of endpoints) {
    const url = `${baseUrl}/${endpoint}`;
    await testCorsConnection(url);
    
    // Add delay between requests
    await new Promise(resolve => setTimeout(resolve, 1000));
  }
};

export const testSpecificEndpoints = async (): Promise<void> => {
  const baseUrl = 'https://apijiggle.safebima.com/api';
  
  // Test the specific endpoints that are failing
  const failingEndpoints = [
    'dashboard?id=653456&key=wrtretvwetwertvwe',
    'upload-history',
  ];
  
  
  for (const endpoint of failingEndpoints) {
    const url = `${baseUrl}/${endpoint}`;
    await testCorsConnection(url);
    
    
    // Add delay between requests
    await new Promise(resolve => setTimeout(resolve, 1000));
  }
};
